import { useState, useEffect, useRef } from 'react';
import { getPerformanceInsights } from '../../utils/performance';

/**
 * Group per-match history entries into daily data points for the chart.
 * Uses actual conservativeRating from TrueSkill calculation (not approximation).
 */
const groupHistoryByDay = (history) => {
  if (!history || history.length === 0) return [];

  const byDate = {};
  history.forEach(entry => {
    const dateKey = new Date(entry.timestamp || entry.date).toLocaleDateString(
      'en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' }
    );
    if (!byDate[dateKey]) {
      byDate[dateKey] = { date: dateKey, matches: [], wins: 0, losses: 0, mu: 0 };
    }
    const isWin = entry.result === 'win';
    if (isWin) byDate[dateKey].wins++;
    else byDate[dateKey].losses++;
    byDate[dateKey].matches.push(
      (isWin ? 'W' : 'L') + (entry.score ? ' ' + entry.score : '')
    );
    // Take the last conservativeRating of the day as the day's rating
    byDate[dateKey].mu = entry.conservativeRating;
  });

  return Object.values(byDate)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(d => ({ ...d, netWins: d.wins > d.losses }));
};

const TrendChart = ({ history, comparisonHistory, player, matches, comparePlayer }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const parentRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!parentRef.current) return;
    const resizeObserver = new ResizeObserver(entries => {
      if (!entries || entries.length === 0) return;
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    resizeObserver.observe(parentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { width, height } = dimensions;
  const paddingY = 20;
  const paddingX = 35;

  // Group real TrueSkill history into daily data points
  const performanceData = groupHistoryByDay(history);

  const insights = getPerformanceInsights(performanceData, matches, player);

  // Handle mouse movement for hover detection
  const handleMouseMove = event => {
    if (!parentRef.current || mainPoints.length === 0) return;

    const rect = parentRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;

    // Find closest point by X coordinate only for better UX on line charts
    let closestPoint = null;
    let closestDistance = Infinity;

    mainPoints.forEach((point, index) => {
      const distance = Math.abs(point.x - mouseX);
      if (distance < 20 && distance < closestDistance) {
        // Snap within 20px
        closestDistance = distance;
        closestPoint = {
          ...point,
          dataIndex: index,
          originalData: performanceData[index],
        };
      }
    });

    setHoveredPoint(closestPoint);
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Generate points for visualization
  const getPoints = data => {
    if (!data || data.length === 0 || width === 0) return [];

    return data.map((h, i) => {
      const x =
        paddingX + (i / Math.max(1, data.length - 1)) * (width - 2 * paddingX);
      const yRange = height - 2 * paddingY;
      const minRating = Math.min(...data.map(d => d.mu));
      const maxRating = Math.max(...data.map(d => d.mu));
      // Minimum range of 5 for better visualization
      const ratingRange = Math.max(5, maxRating - minRating);

      const normalizedRating = (h.mu - minRating) / ratingRange;
      // Add padding top/bottom
      const y =
        height - paddingY - normalizedRating * yRange * 0.8 - yRange * 0.1;

      return {
        x,
        y,
        mu: h.mu,
        date: h.date,
        matches: h.matches,
        wins: h.wins,
        losses: h.losses,
        netWins: h.netWins,
        rawIndex: i,
      };
    });
  };

  const mainPoints = getPoints(performanceData);
  const comparisonData = groupHistoryByDay(comparisonHistory);
  const compPoints = getPoints(comparisonData);

  // Cubic Bezier smoothing
  const getPath = points => {
    if (points.length < 2) return '';
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const curr = points[i];
      const prev = points[i - 1];
      const controlX = (prev.x + curr.x) / 2;
      d += ` C ${controlX} ${prev.y}, ${controlX} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
  };

  return (
    <div
      ref={parentRef}
      className="relative w-full h-full group"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {width > 0 && height > 0 && (
        <svg
          className="w-full h-full overflow-visible cursor-crosshair"
          viewBox={`0 0 ${width} ${height}`}
        >
          {/* Background gradient */}
          <defs>
            <linearGradient
              id="performanceGradient"
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#000" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid Lines */}
          <line
            x1={0}
            y1="30"
            x2={width}
            y2="30"
            stroke="#f4f4f5"
            strokeWidth="1"
            strokeDasharray="2 2"
          />
          <line
            x1={0}
            y1={height / 2}
            x2={width}
            y2={height / 2}
            stroke="#e4e4e7"
            strokeWidth="1"
          />
          <line
            x1={0}
            y1={height - 30}
            x2={width}
            y2={height - 30}
            stroke="#f4f4f5"
            strokeWidth="1"
            strokeDasharray="2 2"
          />

          {/* Comparison Line */}
          {compPoints.length > 0 && (
            <>
              <path
                d={getPath(compPoints)}
                fill="none"
                stroke="#71717a"
                strokeWidth="1.5"
                strokeDasharray="4 4"
                opacity="0.6"
              />
              {compPoints.map(p => (
                <circle
                  key={`comparison-${p.x}-${p.y}`}
                  cx={p.x}
                  cy={p.y}
                  r="2"
                  fill="#71717a"
                  opacity="0.5"
                />
              ))}
            </>
          )}

          {/* Main Player Performance Area */}
          {mainPoints.length > 1 && (
            <path
              d={`${getPath(mainPoints)} L ${mainPoints[mainPoints.length - 1].x},${height - paddingY} L ${mainPoints[0].x},${height - paddingY} Z`}
              fill="url(#performanceGradient)"
            />
          )}

          {/* Main Player Line */}
          {mainPoints.length > 0 && (
            <>
              {/* Only draw path if we have multiple points */}
              {mainPoints.length > 1 && (
                <path
                  d={getPath(mainPoints)}
                  fill="none"
                  stroke="#000"
                  strokeWidth="2.5"
                />
              )}

              {/* Data Points with Win/Loss Indicators - Vercel Colors */}
              {mainPoints.map(p => {
                const performanceDataPoint = performanceData[p.rawIndex];
                const isWinDay = performanceDataPoint?.netWins || false;
                return (
                  <circle
                    key={`main-${p.x}-${p.y}`}
                    cx={p.x}
                    cy={p.y}
                    r="4"
                    fill={isWinDay ? '#00d9ff' : '#ff006e'}
                    stroke="white"
                    strokeWidth="2"
                    className="drop-shadow-sm"
                    style={{
                      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                    }}
                  />
                );
              })}
            </>
          )}

          {/* Cursor indicator */}
          {hoveredPoint && (
            <g>
              <line
                x1={hoveredPoint.x}
                y1={0}
                x2={hoveredPoint.x}
                y2={height}
                stroke="#000"
                strokeWidth="1"
                strokeDasharray="2 2"
                opacity="0.3"
              />
              <circle
                cx={hoveredPoint.x}
                cy={hoveredPoint.y}
                r="6"
                fill="none"
                stroke="#000"
                strokeWidth="2"
              />
            </g>
          )}
        </svg>
      )}

      {/* Tooltip */}
      {hoveredPoint && (
        <div
          className="absolute bg-black text-white text-[10px] p-3 rounded-lg shadow-xl z-50 min-w-[140px] border border-gray-800"
          style={{
            left: `${hoveredPoint.x}px`,
            top: `${Math.max(5, hoveredPoint.y - 40)}px`,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
          }}
        >
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-4">
              <span className="font-semibold text-xs">Rating:</span>
              <span className="font-mono text-xs text-cyan-400">
                {hoveredPoint.mu.toFixed(1)}
              </span>
            </div>

            {(() => {
              const data = hoveredPoint.originalData || hoveredPoint;
              const matches = data.matches || [];
              const wins = data.wins || 0;
              const losses = data.losses || 0;

              return matches.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-300">Record:</span>
                    <span className="font-semibold text-xs">
                      <span className="text-cyan-400">{wins}W</span>
                      <span className="text-gray-400 mx-1">-</span>
                      <span className="text-pink-400">{losses}L</span>
                    </span>
                  </div>

                  {matches.slice(0, 2).map(match => (
                    <div
                      key={`match-${match}`}
                      className="text-[9px] text-gray-400"
                    >
                      {match}
                    </div>
                  ))}

                  {matches.length > 2 && (
                    <div className="text-[8px] text-gray-500">
                      +{matches.length - 2} more
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-gray-300">Result:</span>
                  <span
                    className={`font-semibold text-xs ${data.netWins ? 'text-cyan-400' : 'text-pink-400'}`}
                  >
                    {data.netWins ? 'Net Win' : 'Net Loss'}
                  </span>
                </div>
              );
            })()}

            <div className="text-gray-400 text-center border-t border-gray-700 pt-1.5 mt-1 text-[9px]">
              {hoveredPoint.date}
            </div>
          </div>
          <div className="w-2 h-2 bg-black absolute -bottom-1 left-1/2 -translate-x-1/2 rotate-45 border border-gray-800"></div>
        </div>
      )}

      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-5 text-[9px] text-zinc-400 font-mono pointer-events-none">
        <span>High</span>
        <span>Avg</span>
        <span>Low</span>
      </div>

      {/* Performance Insights */}
      <div className="absolute top-0 right-2 bg-white/90 backdrop-blur-sm rounded px-2 py-1 text-[9px] font-bold text-zinc-600 border border-zinc-200">
        <div className="flex items-center gap-2">
          <span
            className={`${insights.trend === 'Improving' ? 'text-emerald-600' : insights.trend === 'Declining' ? 'text-red-600' : 'text-zinc-600'}`}
          >
            {insights.trend} ({insights.change > 0 ? '+' : ''}
            {insights.change})
          </span>
          {insights.streak > 1 && (
            <span
              className={`${insights.isWinStreak ? 'text-emerald-600' : 'text-red-600'}`}
            >
              {insights.streak}
              {insights.isWinStreak ? 'W' : 'L'} streak
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendChart;
